/**
 * Pure visual-viewport → keyboard-frame computation (no React, no DOM access —
 * so it unit-tests in isolation without the react-intl ESM chain the modal
 * shell drags in). The hook (`useVisualViewportFrame`) feeds it the live
 * measurements plus whether an editable element currently holds focus.
 *
 * Why the `editableFocused` gate is load-bearing (the stuck-modal P0):
 * on an installed iOS PWA, `window.visualViewport.height` reports a STALE/short
 * value after the software keyboard dismisses, and the corrective `resize`
 * event frequently never fires in standalone mode. A height-only heuristic
 * (`vv.height < layoutH * 0.78`) therefore gets STUCK reading "keyboard open"
 * forever, so the modal never returns to its centered resting frame — it stays
 * top-pinned + height-capped (the "modal size changes after I open/close the
 * keyboard" report). In mobile Safari the event fires cleanly, which is exactly
 * why the bug shows up in the PWA but not the browser.
 *
 * The fix is a hard invariant the OS can't lie about: the keyboard cannot be
 * open unless an editable element is focused. The moment focus leaves the field
 * (focusout on keyboard dismiss), `keyboardOpen` is false regardless of any
 * stale measurement, so the reframe resets deterministically.
 */

/** Obscured layout-viewport height that usually means a software keyboard is open. */
export const KEYBOARD_OBSCURED_PX = 50;

/**
 * @param {{innerHeight:number, vvHeight:?number, vvOffsetTop?:number, editableFocused:boolean}} m
 * @returns {{keyboardOpen:boolean, offsetTop:number, height:number, obscured:number}}
 */
export function computeViewportFrame({
  innerHeight,
  vvHeight,
  vvOffsetTop = 0,
  editableFocused,
}) {
  const layoutH = innerHeight || 0;
  // No visualViewport → fall back to the layout height, never "open".
  const height = vvHeight == null ? layoutH : vvHeight;
  const obscured = Math.max(0, layoutH - height - vvOffsetTop);
  const measuredOpen = obscured > KEYBOARD_OBSCURED_PX
    || (layoutH > 0 && height < layoutH * 0.78);
  return {
    // Gate on the focus invariant — stale measurements can never strand it open.
    keyboardOpen: !!editableFocused && measuredOpen,
    offsetTop: vvOffsetTop,
    height,
    obscured,
  };
}

/**
 * How long a changed `visualViewport.offsetTop` must hold steady before the
 * overlay reframe follows it. iOS transiently PANS the visual viewport when a
 * field is focused ("reveal the input"), then reverts the pan once the keyboard
 * settles — a fixed, scroll-locked modal that mirrors every intermediate value
 * rides that pan down and back up (the "modal shifts down then returns" bounce).
 * A pan that SURVIVES this window is a real, sustained pan and is honored.
 */
export const OFFSET_TOP_SETTLE_MS = 280;

/**
 * Damp transient offsetTop changes out of the viewport frame (pure — the hook
 * owns the clock and the settle timer).
 *
 * Contract (revised 2026-08-20, round 4 — device-log-proven):
 *  - **Zero is the resting truth, and a raw offsetTop of exactly 0 is ALWAYS
 *    committed immediately, in every state** — open, closed, mid-probation,
 *    at the open flip, doesn't matter. A `position:fixed` shell's natural
 *    offsetTop is 0; a pan is always an excursion AWAY from 0, never TO it,
 *    so there is nothing to distrust about a fresh 0 reading. This is the
 *    fix for a real device log (delete-confirm, autoFocus): a transient pan
 *    (`ot=299`) arrived while the frame still read CLOSED, and the OLD rule
 *    (`!raw.keyboardOpen` commits raw verbatim) let it commit as truth; that
 *    corrupted `prev.offsetTop`, which the open-flip's `held` fallback
 *    (below) then inherited — the modal opened pinned to a stale 299px
 *    band-top (clamp computed too tall), then ~300ms later the real settle
 *    corrected it — the founder's "shows full size, then shrinks" two-step.
 *    Committing 0 unconditionally means a corrupted nonzero value can never
 *    survive past the next real (0) reading.
 *  - **A NONZERO raw offsetTop always goes through the pending/probation
 *    path — including while the keyboard is CLOSED.** The old shortcut
 *    (`!raw.keyboardOpen` → commit raw) is what let the 299px pan above
 *    commit while closed in the first place; no state should trust an
 *    unconfirmed nonzero pan on sight.
 *  - The open flip itself still commits `keyboardOpen`/`height` from `raw`
 *    instantly — the reframe must never lag the keyboard — while `offsetTop`
 *    only ever lands on 0 (the zero rule above) or on a nonzero value that
 *    already survived probation (the `held` branch below, same as any other
 *    in-flight pan).
 *  - While the keyboard stays open, height/obscured changes flow through, but a
 *    CHANGED offsetTop is held (frame keeps the committed offsetTop, obscured
 *    re-derived so the band math stays coherent) until the same value has been
 *    observed for `settleMs`. A bounce (0 → 80 → 0) therefore never commits —
 *    and per the zero rule, the "→ 0" leg of that bounce commits instantly
 *    rather than waiting out its own probation.
 *
 * @param {{prev:?object, raw:object, pending:?{offsetTop:number, since:number}, now:number, settleMs?:number}} a
 * @returns {{frame:object, pending:?{offsetTop:number, since:number}}}
 */
export function resolveDampedFrame({
  prev, raw, pending, now, settleMs = OFFSET_TOP_SETTLE_MS,
}) {
  // Zero always wins, unconditionally — see the contract above. Checked
  // FIRST, ahead of every other branch, so a corrupted nonzero `prev` (or a
  // pan still on probation) can never outlive the next zero reading.
  if (raw.offsetTop === 0) {
    return { frame: raw, pending: null };
  }
  // First read of a session (or just re-enabled): nothing to damp against —
  // commit raw so an already-panned mount (e.g. a modal mounting under an
  // already-open, already-panned keyboard) positions correctly. `raw.offsetTop`
  // is guaranteed nonzero here (the zero case returned above).
  if (!prev) {
    return { frame: raw, pending: null };
  }
  // Steady offsetTop → nothing to damp; measurements flow through.
  if (raw.offsetTop === prev.offsetTop) {
    return { frame: raw, pending: null };
  }
  const samePending = pending && pending.offsetTop === raw.offsetTop;
  if (samePending && now - pending.since >= settleMs) {
    // The pan survived the settle window — it's real; follow it.
    return { frame: raw, pending: null };
  }
  // Freeze the WHOLE committed geometry while the pan is on probation. When
  // the keyboard was already open, prev is a complete valid frame — reuse it
  // verbatim: a transient pan wobbles MORE than offsetTop (device log #2,
  // 2026-08-20: Safari collapsed its toolbar mid-pan, so the derived obscured
  // reading bounced 349→245→349 and re-deriving it from raw inputs breathed
  // ~100px of bottom padding — the residual jank). Nothing may move until the
  // measurements either revert or survive the settle window. On the OPEN FLIP
  // there is no prior open geometry to freeze, so approximate: hold offsetTop
  // and fold the held-back pan into obscured (the next steady event corrects
  // once, and the flip itself must not lag the keyboard).
  const held = prev.keyboardOpen ? prev : {
    ...raw,
    offsetTop: prev.offsetTop,
    obscured: Math.max(0, raw.obscured + (raw.offsetTop - prev.offsetTop)),
  };
  return {
    frame: held,
    pending: samePending ? pending : { offsetTop: raw.offsetTop, since: now },
  };
}

/**
 * An element whose focus would raise the SOFTWARE KEYBOARD (text entry).
 * Deliberately excludes <select> — its picker wheel is not a keyboard and can
 * retain focus after dismissal, which would re-strand `keyboardOpen` true.
 */
export function isEditableElement(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  return !!el.isContentEditable;
}

/**
 * True only when the focused editable lives INSIDE the given modal subtree.
 *
 * Why scope to the container, not the whole document: a modal must reframe for
 * ITS OWN keyboard. The global "any editable focused" check stranded an
 * unrelated dialog — the "mark as paid" confirm (no input of its own) got pinned
 * under the status bar because a background field (the invoices search box) kept
 * focus while the iOS PWA reported a stale-short viewport. With containment, a
 * no-input confirm never reframes, and a real form modal still reframes when its
 * own field is focused. `container` is null until the dialog node mounts → not
 * ours yet → no reframe (the safe default while a modal is just appearing).
 *
 * Callers wanting the GLOBAL signal (BottomTabBar) skip this and use
 * isEditableElement directly.
 */
export function isEditableFocusedWithin(activeEl, container) {
  if (!isEditableElement(activeEl)) return false;
  if (!container || typeof container.contains !== 'function') return false;
  return container.contains(activeEl);
}
